// =============================================================================
// domain/confluences.ts — the confluence registry, and how a trade stores marks.
// -----------------------------------------------------------------------------
// "Which tools confirmed this setup." A user-editable list living in
// `user_preferences.confluences`, one level deep: a parent may carry `children`,
// and every id — parent or child — is unique across the whole tree, because the
// per-trade store is FLAT: `{ [id]: { active: true } }` holds either.
//
// ⚠ THE REGISTRY WAS ALREADY BEING LOADED. `preferences.confluences` is typed
// `unknown[]` and has been read since the preferences loader shipped; the handoff
// recorded it as "not loaded" and that was wrong. Nothing was missing but the
// parsing — which is this file.
//
// ── 🚨 THREE RULES FROM `commitTrade`, EACH LOAD-BEARING ──
//
// 1. ONLY ACTIVE MARKS ARE STORED. There is no `{ active: false }`. Unchecking
//    removes the key. Same asymmetry as the discipline booleans, for a different
//    reason: a stored false would say "this tool was looked at and did not
//    confirm", which is a judgment nobody made.
//
// 2. A CHECKED CHILD IMPLIES ITS PARENT. The parent's own checkbox is DERIVED
//    when it has children — the old client makes it `pointer-events:none` — so a
//    parent with children is a CATEGORY, and analytics never rank it (counting
//    both would double-count the same signal).
//
// 3. 🚨 ORPHANS ARE CARRIED FORWARD VERBATIM. A confluence deleted from the
//    registry in Settings still exists on every trade that was tagged with it.
//    The form cannot render it, so the form must not decide it is gone: the old
//    client's comment is "don't silently delete history when the list is edited".
//    Drop this and editing the registry quietly rewrites the past — and the loss
//    is invisible, because the id it deleted is the one nothing can display.
// =============================================================================

/** A confluence node. Children are one level deep and carry no children of their own. */
export interface ConfluenceNode {
  id: string;
  label: string;
  /** Hover tooltip on the parent. Absent on children. */
  hint?: string;
  children: readonly ConfluenceChild[];
}

export interface ConfluenceChild {
  id: string;
  label: string;
}

/**
 * The seed for a fresh install, transcribed from the old client's
 * `DEFAULT_CONFLUENCES`.
 *
 * ⚠ These are DEBRANDED generics on purpose (v2.6.1). Josh's own live list is
 * different — `GEX / DEX`, `JTF Key Levels`, `JTF Multi VWAP`, `JTF VolPro`,
 * `JTF Orderflow`, `JTF Technicals`, `STRUCTURED S/D`, `INST-CON-ES` — because
 * it was seeded before the debrand and has been edited since. So this constant
 * is a FALLBACK, never the thing to render: `confluenceRegistry` reads the
 * stored list first, and testing against these defaults would pass while showing
 * every real user someone else's toolkit.
 */
export const DEFAULT_CONFLUENCES: readonly ConfluenceNode[] = [
  { id: 'key-levels', label: 'Key Levels', hint: 'e.g. ON high, prior-day VAH, IB high, ORB break', children: [] },
  { id: 'multi-vwap', label: 'Multi VWAP', hint: 'e.g. 1m + 5m + session VWAP all aligned long; AVWAP from CPI', children: [] },
  { id: 'volume-profile', label: 'Volume Profile', hint: 'e.g. sitting above POC, naked VPOC overhead, single prints', children: [] },
  { id: 'orderflow', label: 'Orderflow', hint: 'e.g. delta flip, sniper signal, absorption print, footprint imbalance', children: [] },
  { id: 'icebergs', label: 'Icebergs', hint: 'e.g. iceberg buyer at 5068; absorption visible at the bid', children: [] },
  { id: 'gex-dex', label: 'GEX / DEX', hint: 'e.g. positive gamma wall at 5070; large 0DTE puts below VAL', children: [] },
];

const str = (v: unknown): string => (typeof v === 'string' ? v : '');

function parseChild(v: unknown): ConfluenceChild | null {
  if (!v || typeof v !== 'object') return null;
  const r = v as Record<string, unknown>;
  const id = str(r['id']);
  if (!id) return null;
  return { id, label: str(r['label']) || str(r['name']) || id };
}

function parseNode(v: unknown): ConfluenceNode | null {
  if (!v || typeof v !== 'object') return null;
  const r = v as Record<string, unknown>;
  const id = str(r['id']);
  if (!id) return null;
  const kids = Array.isArray(r['children'])
    ? (r['children'] as unknown[]).map(parseChild).filter((c): c is ConfluenceChild => c !== null)
    : [];
  const hint = str(r['hint']);
  return {
    id,
    // ⚠ Falls back to the raw id rather than to an empty string. A confluence
    // rendered as a nameless checkbox is worse than one rendered as `gex-dex`.
    label: str(r['label']) || str(r['name']) || id,
    ...(hint ? { hint } : {}),
    children: kids,
  };
}

/**
 * The list to RENDER, from the stored preference.
 *
 * ⚠ The old client's `getConfluences()` falls back to the defaults only when the
 * stored value is not an array — and separately, `migrateState` reseeds an EMPTY
 * stored array with the defaults. So an empty list never survives to the form.
 * Both branches are here, and the reason to keep them is that they differ: an
 * unparseable value is a corrupt preference, an empty one is a user who cleared
 * the list, and both end at the same defaults but only one of them is a bug.
 */
export function confluenceRegistry(stored: unknown): readonly ConfluenceNode[] {
  if (!Array.isArray(stored)) return DEFAULT_CONFLUENCES;
  const parsed = (stored as unknown[]).map(parseNode).filter((n): n is ConfluenceNode => n !== null);
  return parsed.length ? parsed : DEFAULT_CONFLUENCES;
}

/** Every id in the tree — parents and children — for the orphan check. */
export function allConfluenceIds(registry: readonly ConfluenceNode[]): ReadonlySet<string> {
  const out = new Set<string>();
  for (const c of registry) {
    out.add(c.id);
    for (const ch of c.children) out.add(ch.id);
  }
  return out;
}

/**
 * A parent that HAS children is a CATEGORY, not a confluence in its own right.
 *
 * Ported because analytics must not count it: its active children carry the
 * signal, so ranking the parent too double-counts one observation.
 */
export function isConfluenceCategory(registry: readonly ConfluenceNode[], id: string): boolean {
  const c = registry.find((n) => n.id === id);
  return !!c && c.children.length > 0;
}

/** The flat set of ids marked active on a trade — what the checkboxes render from. */
export function activeConfluenceIds(stored: unknown): ReadonlySet<string> {
  const out = new Set<string>();
  if (!stored || typeof stored !== 'object') return out;
  for (const [id, v] of Object.entries(stored as Record<string, unknown>)) {
    if (v && typeof v === 'object' && (v as Record<string, unknown>)['active'] === true) out.add(id);
    // ⚠ A bare `true` is accepted as well. Test fixtures in this repo write
    // `confluences: { vwap: true }`, and a real row could too — the column is
    // untyped JSONB. Reading only the object form would silently render an
    // active confluence as unchecked, and the next save would then clear it.
    else if (v === true) out.add(id);
  }
  return out;
}

/**
 * Build the value to STORE, from the checkbox state and what was there before.
 *
 * @param registry what the form actually rendered
 * @param checked  the ids the user has ticked (parents and/or children)
 * @param previous the trade's stored `confluences`, for the orphan carry-forward
 */
export function confluencesToStore(
  registry: readonly ConfluenceNode[],
  checked: ReadonlySet<string>,
  previous: unknown,
): Record<string, { active: true }> {
  const out: Record<string, { active: true }> = {};

  for (const c of registry) {
    let parentActive = checked.has(c.id);
    for (const ch of c.children) {
      if (checked.has(ch.id)) {
        out[ch.id] = { active: true };
        parentActive = true;   // a child implies its parent
      }
    }
    if (parentActive) out[c.id] = { active: true };
  }

  // ⚠ THE ORPHAN CARRY-FORWARD. Ids the registry no longer contains were never
  // on screen, so the user cannot have unchecked them — and a form must not
  // delete what it could not show.
  const visible = allConfluenceIds(registry);
  if (previous && typeof previous === 'object') {
    for (const [id, v] of Object.entries(previous as Record<string, unknown>)) {
      if (!visible.has(id)) out[id] = v as { active: true };
    }
  }

  return out;
}
